/**
 * Re-authenticates in a popup so an expiring session can be renewed without
 * reloading the portal (and losing whatever the user has open).
 *
 * The portal never holds a refresh token: Google/Cognito redirect to the Aqfer
 * auth service, which exchanges the code and sends the browser on to
 * `success_redirect_url` with a fresh Aqfer token. Pointing that redirect at
 * our own callback route lets the popup come home same-origin and hand the
 * token back.
 *
 * The token travels over a BroadcastChannel, not `window.opener.postMessage`.
 * When the provider renders an interactive sign-in page (account chooser,
 * email prompt) it can send a Cross-Origin-Opener-Policy header, which severs
 * the popup from its opener for good: `opener` is null when the popup gets
 * home, and `popup.closed` reads true in the portal from that moment. A
 * BroadcastChannel reaches every same-origin window regardless — including
 * other portal tabs, which then pick up the renewed token too.
 *
 * The popup is opened *before* the login URL is fetched, because
 * `window.open` after an `await` is blocked as a pop-up.
 */

const POPUP_FEATURES = 'width=520,height=680,menubar=no,toolbar=no,location=yes'

/** Must match the route path in router/index.js. */
export const SESSION_REFRESH_CALLBACK_PATH = '/auth/session-callback'

/** BroadcastChannel name and message type; must match the callback view. */
export const SESSION_REFRESH_CHANNEL = 'portal:session-refresh'
export const SESSION_REFRESH_MESSAGE = 'portal:session-refresh-callback'

export function sessionRefreshCallbackUrl() {
    return window.location.origin + SESSION_REFRESH_CALLBACK_PATH
}

/**
 * Subscribe to results from the callback page.
 * @param {Function} onResult - called with `{ token }` or `{ error }`
 * @returns {Function} unsubscribe
 */
export function listenForSessionRefresh(onResult) {
    const channel = new BroadcastChannel(SESSION_REFRESH_CHANNEL)
    channel.onmessage = (event) => {
        const data = event.data
        if (!data || data.type !== SESSION_REFRESH_MESSAGE) return
        onResult(data.token ? { token: data.token } : { error: data.error || 'Sign-in did not return a new session.' })
    }
    return () => channel.close()
}

/**
 * Open the popup and point it at the provider login.
 * Must be called synchronously from a click handler.
 *
 * Deliberately returns once the popup is navigating: the result arrives via
 * {@link listenForSessionRefresh}, and `popup.closed` cannot be trusted to
 * mean the user gave up (see the module comment).
 *
 * @param {Function} buildUrl - resolves to the provider login URL whose
 *   success redirect is {@link sessionRefreshCallbackUrl}
 */
export async function openSessionRefreshPopup(buildUrl) {
    const popup = window.open('', 'aqfer-session-refresh', POPUP_FEATURES)
    if (!popup) {
        throw new Error('Your browser blocked the sign-in window. Allow pop-ups for this site and try again.')
    }
    try {
        popup.location.href = await buildUrl()
    } catch (e) {
        try { popup.close() } catch (err) { /* already gone */ }
        throw e
    }
}
